// Instanced vegetation per district greenery (v1 scattered individual meshes
// in world-manager createWildVegetation/createRoundBush etc.; v2 uses three
// InstancedMeshes per tile: trunks, crowns, bushes — the plan's instancing
// upgrade applied from the start).

import * as THREE from 'three';
import type { DistrictDef } from '@/content/districts';
import type { Rng } from '@/core/rng';
import { InstancedSet } from '@/render/instanced-set';

const TILE_LENGTH = 80;

const trunkGeo = new THREE.CylinderGeometry(0.09, 0.13, 1.1, 6);
const crownGeo = new THREE.SphereGeometry(0.75, 8, 6);
const bushGeo = new THREE.SphereGeometry(0.35, 7, 5);

const trunkMat = new THREE.MeshLambertMaterial({ color: 0x6b4423 });
const crownMat = new THREE.MeshLambertMaterial({ color: 0x2d7a2d });
const bushMat = new THREE.MeshLambertMaterial({ color: 0x3c8c3c });

export function buildVegetationIntoTile(tile: THREE.Group, district: DistrictDef, rng: Rng): void {
  const greenery = district.greenery;
  const treeCount = Math.round(greenery * 22);
  const bushCount = Math.round(greenery * 30);

  const trunks = new InstancedSet(trunkGeo, trunkMat, treeCount);
  const crowns = new InstancedSet(crownGeo, crownMat, treeCount);
  const bushes = new InstancedSet(bushGeo, bushMat, bushCount);

  for (let i = 0; i < treeCount; i++) {
    // Trees on the strip between pavement edge and buildings (|x| 6.5–7.6)
    const side = rng.next() < 0.5 ? -1 : 1;
    const x = side * (6.5 + rng.next() * 1.1);
    const z = -rng.next() * TILE_LENGTH;
    const scale = 0.8 + rng.next() * 0.7;
    trunks.add(x, 0.55 * scale, z, scale);
    crowns.add(x, 1.35 * scale, z, scale);
  }

  for (let i = 0; i < bushCount; i++) {
    const side = rng.next() < 0.5 ? -1 : 1;
    const x = side * (6.2 + rng.next() * 2.0);
    const z = -rng.next() * TILE_LENGTH;
    const scale = 0.7 + rng.next() * 0.8;
    bushes.add(x, 0.28 * scale, z, scale, rng.next() * Math.PI, scale * 0.8);
  }

  tile.add(trunks.mesh, crowns.mesh, bushes.mesh);
}
